import { PublicClientApplication } from '@azure/msal-browser';
import { getScopeMap } from 'auth-config';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { companiesSelector as selector, companyThunks as thunk } from 'store/reducers/companiesSlice';
import { setIsAdmin, userSelector, userThunks } from 'store/reducers/usersSlice';
import AppContent from './AppContent';
import { setMsalInstance } from './services/msalInstanceHolder';
import type { AppDispatch } from './store';

const App = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [pca, setPca] = useState<PublicClientApplication | null>(null);
    const [companyInitialized, setCompanyInitialized] = useState(false);

    const companies = useSelector(selector.selectCompanies);
    const userProfile = useSelector(userSelector.selectUserProfile);

    useEffect(() => {
        const fetchConfig = async () => {
            const res = await fetch('/config');
            const config = await res.json();

            const msalConfig = {
                auth: {
                    clientId: config.azureClientId,
                    authority: config.azureAuthority,
                    redirectUri: window.location.origin,
                    postLogoutRedirectUri: window.location.origin,
                },
                cache: {
                    cacheLocation: 'localStorage',
                    storeAuthStateInCookie: false,
                },
            };

            window.apiScopes = config.apiScopes;

            const pcaInstance = new PublicClientApplication(msalConfig);
            await pcaInstance.handleRedirectPromise();

            const response = await pcaInstance.handleRedirectPromise();
            const accounts = pcaInstance.getAllAccounts();

            if (response) {
                pcaInstance.setActiveAccount(response.account);
            } else if (accounts.length > 0) {
                pcaInstance.setActiveAccount(accounts[0]);
            } else {
                // No accounts, trigger redirect login
                await pcaInstance.loginRedirect({
                    ...getScopeMap().returnCenter,
                    prompt: 'login',
                });
                return;
            }

            // Set the msal instance to be reused in api.ts
            setMsalInstance(pcaInstance);

            const activeAccount = pcaInstance.getActiveAccount();
            const roles = (activeAccount?.idTokenClaims as Record<string, unknown>)?.roles;
            if (Array.isArray(roles) && roles.some((r: string) => r.toLowerCase() === 'admin')) {
                dispatch(setIsAdmin(true));
            }

            setPca(pcaInstance);
        };

        fetchConfig();
    }, []);

    // User profile needs to be loaded first (display name, language, etc.).
    useEffect(() => {
        if (pca) {
            dispatch(userThunks.getUserProfile());
        }
    }, [pca, dispatch]);

    useEffect(() => {
        if (pca && userProfile) {
            // Get companies after user profile is loaded
            dispatch(thunk.getCompanies());
            dispatch(userThunks.getAllDialogPreferences());
        }
    }, [pca, dispatch, userProfile]);

    // Initialize company when companies are loaded
    useEffect(() => {
        if (companies && companies.length > 0 && !companyInitialized) {
            dispatch(thunk.initializeCompany(companies)).then(() => {
                setCompanyInitialized(true);
                // Refresh app display settings when company is initialized
                dispatch(thunk.getAppDisplaySettings());
            });
        }
    }, [companies, companyInitialized, dispatch]);

    if (!pca || !companies || companies.length === 0 || !userProfile || !companyInitialized) {
        return <div>Loading...</div>;
    }

    return <AppContent pca={pca} companies={companies} />;
};

export default App;
